import { useContext, useEffect, useState } from "react";
import { useAppLoading } from "../components/layout/app-loading-provider";
import { CognitoContext } from "../contexts/cognito/cognito-provider";
import { CurrentCompanyStorage, mapGroupCompanyOptions } from "../library/core/current-company";
import { CoreServiceSelector } from "../services/core/service-selector";

/**
 * Encapsulates all data-fetching and company-selection logic for the
 * Redemption History page.
 *
 * Returns:
 *   data            – array of redemption rows
 *   pageState       – { isLoading, hasErrors }
 *   unauthorised    – { show: boolean }
 *   selectedCompany – { companyId }
 *   companyOptions  – props object for <CompanySelectInput>
 *   onSetTargetCompany(companyId) – switch the active company
 */
export const useRedemptionHistory = () => {
  const [data, setData] = useState([]);
  const [pageState, setPageState] = useState({ isLoading: true });
  const [unauthorised, setUnauthorised] = useState({ show: false });
  const [selectedCompany, setSelectedCompany] = useState(() => ({
    companyId: CurrentCompanyStorage.getCurrentCompanyId(),
  }));
  const [companyOptions, setCompanyOptions] = useState({});

  const { showLoading, hideLoading } = useAppLoading();
  const { state } = useContext(CognitoContext);
  const service = CoreServiceSelector.GetRedemptionService(state.session);

  const getRedemptionHistory = async (companyId) => {
    if (!service) return;

    const CompanyId =
      companyId ||
      selectedCompany?.companyId ||
      CurrentCompanyStorage.getCurrentCompanyId() ||
      state?.userInfo?.CompanyId;

    if (!CompanyId) return;

    setPageState((prev) => ({ ...prev, isLoading: true }));
    showLoading();

    const result = await service
      .getRedemptionsHistory(CompanyId)
      .catch((err) => {
        if (err?.response?.status === 401) setUnauthorised({ show: true });
      });

    hideLoading();
    setData(result || []);
    setPageState({ isLoading: false });
  };

  const onSetTargetCompany = async (companyId) => {
    CurrentCompanyStorage.saveCurrentCompanyId(companyId);
    setSelectedCompany({ companyId });
    await getRedemptionHistory(companyId);
  };

  // Bootstrap: build company options and do initial load when userInfo arrives
  useEffect(() => {
    if (!state.userInfo) {
      getRedemptionHistory();
      return;
    }

    const userInfo = state.userInfo;
    const storedCompanyId = CurrentCompanyStorage.getCurrentCompanyId();
    const hasStoredCompany = userInfo.GroupCompanies.some(
      (m) => `${m.Id}` === `${storedCompanyId}`
    );
    const preferredCompanyId = hasStoredCompany
      ? storedCompanyId
      : userInfo.CompanyId;

    const gOptions = {
      options: mapGroupCompanyOptions(userInfo.GroupCompanies),
      defaultCompanyId: preferredCompanyId,
      readOnly:
        !userInfo.HasFullGroupAccess || userInfo.GroupCompanies.length < 2,
      showButton: false,
      buttonText: "View History",
      onButtonClick: async (companyId) => {
        await getRedemptionHistory(companyId);
      },
    };

    setSelectedCompany({ companyId: preferredCompanyId });
    setCompanyOptions(gOptions);
    CurrentCompanyStorage.saveCurrentCompanyId(preferredCompanyId);
    getRedemptionHistory(preferredCompanyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return {
    data,
    pageState,
    unauthorised,
    selectedCompany,
    companyOptions,
    onSetTargetCompany,
  };
};
